import React, { useMemo, useState } from "react";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import notesIcon from "../../assets/componentsIcons/dropdown/doc.svg";
import {
  DisplayColorEnum,
  EntityType,
  LeaseAgreementStatusTypesEnum,
  NotesEntityTypesEnum,
  TenantVM,
} from "@propertelligent/client-api";
import LeaseIcon from "../../assets/svgs/unitIcons/leaseIcon";
import unitsIcon from "../../assets/componentsIcons/listings/units.svg";
import vacanciesIcon from "../../assets/componentsIcons/listings/vacancies.svg";
import ArrowDownIcon from "../../assets/svgs/last-transactions/leaseIcon";
import ActionMenu from "../actionMenus/action-menu";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import AddNotesForm from "../notes/AddNotesForm";
import { useNavigate } from "react-router-dom";
import { GetImages } from "../../utils/GetImages";
import Commonpopup from "../Common-popup/CommonPopup";
import SelectType from "../../components/tenants/common/SelectType";
import { useLeaseInfoTooltip } from "../../hooks/useLeaseInfoTooltip";
import UtilHelper from "../../utils/UtilHelper";
import CalenderIcon from "../../assets/svgs/calenderIcon";
import AddPaymentModal from "../payments/AddPaymentModal";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import displayColorMap from "../../utils/displayColorMap";
import LargeTooltip from "../tooltips/large-tooltip";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import { usePermissions } from "../../hooks/usePermissions";

interface CurrentTenantCardItemProps {
  listingData: TenantVM;
  IsFooter?: boolean;
  showConfirmation?: string | null;
  setShowConfirmation?: (id: string | null) => void;
  handleDelete?: (entityId: any) => void;
  isDeletingTenant?: boolean;
  onEdit?: (unit: TenantVM) => void;
  className?: string;
  onPaymentAdded?: () => void;
}

export const footerData = [
  { iconSrc: unitsIcon, label: "Lease", value: "Expiring in 9 Months" },
  { iconSrc: vacanciesIcon, label: "Tenancy", value: "Since Nov 22, 2023" },
];

const CurrentTenantCardItem: React.FC<CurrentTenantCardItemProps> = ({
  listingData,
  IsFooter = true,
  showConfirmation,
  setShowConfirmation,
  handleDelete,
  isDeletingTenant = false,
  className = "",
  onPaymentAdded,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isRenewOpen, setRenewOpen] = useState(false);
  const [selectLease, setSelectLease] = useState<string>("0");
  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const {
    handleLeaseInfoClick, // we'll rename for clarity
    LeaseInfoTooltip, // ← you can remove this old one if you want
    tooltipState,
    setTooltipState,
    handleCloseTooltip,
  } = useLeaseInfoTooltip();

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property,
  });

  // let isDisabled = listingData?.leaseAgreementStatus === LeaseAgreementStatusTypesEnum.RenewalInProgess ||
  //   listingData?.leaseAgreementStatus === LeaseAgreementStatusTypesEnum.MoveOutInProgress;

  let isDisabled =
    listingData?.leaseAgreementStatus ===
      LeaseAgreementStatusTypesEnum.RenewalInProgess ||
    listingData?.leaseAgreementStatus ===
      LeaseAgreementStatusTypesEnum.MoveOutInProgress ||
    !listingData?.canMoveOrRenew;

  const handleTenantLeaseInfoClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    // Reuse the same handler — it already knows how to show tenant + lease info
    handleLeaseInfoClick(e, listingData);
  };

  const menuItems = useMemo(() => {
    const items = [];

    if (canWrite("Tenant")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: (Id: any) =>
          navigate("/tenants/edit/" + Id, {
            state: { from: window.location.pathname, activeTab: "Tenants" },
          }),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (Id: number) => {
          setShowConfirmation?.(String(Id));
        },
        isDivider: false,
        isDestructive: true,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Payments")) {
      items.push({
        icon: BluePayableIcon,
        label: "Add Payment",
        action: (tenantId) => {
          setPaymentModalConfig({
            isOpen: true,
            entityId: Number(tenantId),
            entityType: EntityType.Tenant,
          });
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    if (canWrite("Tenant")) {
      items.push({
        icon: notesIcon,
        label: "Add Notes",
        action: (tenantId: string) => {
          setModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: GetImages.GreyForwardArrow,
        label: "Move Out",
        action: (Id: Number) => {
          navigate("/tenants/move-out/" + Id);
        },
        disabled: isDisabled,
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: notesIcon,
        label: "Renew Lease",
        disabled: isDisabled,
        action: () => setRenewOpen(true),
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [setShowConfirmation, listingData, canWrite, navigate, isDisabled]);

  const leaseColor =
    displayColorMap[
      listingData?.leaseDisplayTextColor ?? DisplayColorEnum.Default
    ];

  return (
    <>
      <div className={`listing-card ${className}`}>
        <div
          className="listing-link"
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="listing-card-header">
            <div className="tenant-avatar">
              <div className="tenant-initials">
                {UtilHelper.getInitials(
                  listingData?.firstName + " " + listingData?.lastName,
                )}
              </div>
            </div>
            <div className="listing-details">
              <div className="listing-info-container">
                <div className="listing-info">
                  <h3 className="listing-address">
                    {listingData.firstName} {listingData.lastName}
                  </h3>
                  <p className="listing-type">{listingData?.streetAddress}</p>
                  <p className="listing-type">
                    {listingData?.email} • {listingData?.formatted?.PhoneNumber}
                  </p>
                </div>

                <div className="financial-metrics">
                  <div className="metric">
                    <CurrencyTooltip
                      content={listingData?.formatted?.RentRaw}
                      formatted={listingData?.formatted?.Rent}
                    >
                      <p className="metric-value">
                        {listingData?.formatted?.Rent}
                      </p>
                    </CurrencyTooltip>
                    <p className="metric-label">Rent</p>
                  </div>

                  {listingData?.formatted?.LeaseTypeId && (
                    <>
                      <div className="separator-vertical" />
                      <div className="metric">
                        <p className="metric-value">
                          {listingData.formatted.LeaseTypeId ===
                          "Month-to-Month"
                            ? "MTM"
                            : listingData.formatted.LeaseTypeId}
                        </p>
                        <p className="metric-label">Lease Type</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {IsFooter && (
          <>
            <div className="separator-horizontal" />
            <div className="listing-card-footer">
              <div className="listing-stats">
                <div className="listing-stats">
                  <div className="stat">
                    <LeaseIcon
                      fill={leaseColor}
                      className="stat-icon remove-icon-filter"
                      width="24"
                      height="24"
                    />
                    <span className="stat-text" style={{ color: leaseColor }}>
                      {(listingData as any)?.leaseDisplayText}

                      {/* ──── NEW WRAPPER + LargeTooltip ──── */}
                      <div className="info-tooltip-wrapper">
                        <a
                          href="#"
                          className="listing-viewmore mx-2 ml-1 text-blue-500 underline"
                          onClick={handleTenantLeaseInfoClick} // or keep your old name
                        >
                          <img
                            src={GetImages.infoBlackIcon}
                            alt="info-icon"
                            width={16}
                            height={16}
                            className="stat-icon"
                          />
                        </a>

                        {tooltipState.isOpen && (
                          <LargeTooltip
                            isOpen={true}
                            onClose={handleCloseTooltip}
                            description={tooltipState.description}
                            position={tooltipState.position}           // hook usually manages position
                            title={tooltipState.title || "Lease & Tenant Info"}
                            type="tenant"
                          />
                        )}
                      </div>
                    </span>
                  </div>

                  {listingData?.tenancySince && (
                    <div className="stat">
                      <CalenderIcon className="stat-icon" />
                      <span className="stat-text">
                        Tenancy: {listingData.tenancySince}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="footer-actions">
                {listingData?.incomeDue > 0 && (
                  <div
                    className="highlight-value"
                    style={{ color: "var(--color-success)" }}
                  >
                    <ArrowDownIcon
                      width="16"
                      height="16"
                      className="highlight-icon"
                      fill="var(--color-success)"
                    />
                    Past Due:&nbsp;
                    <CurrencyTooltip
                      content={listingData?.formatted?.IncomeDueRaw}
                      formatted={listingData?.formatted?.IncomeDue}
                    >
                      <span className="highlight-text">
                        {listingData?.formatted?.IncomeDue}
                      </span>
                    </CurrencyTooltip>
                  </div>
                )}
                {menuItems.length > 0 && (
                  <ActionMenu
                    menuItems={menuItems}
                    Id={listingData.id}
                    trigger={
                      <button className="action-button">
                        <img
                          src={dropdownIcon}
                          alt="More options"
                          width={20}
                          height={20}
                          className="action-icon"
                        />
                      </button>
                    }
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {isModalOpen && (
        <AddNotesForm
          entityId={listingData?.id}
          entityTypeId={NotesEntityTypesEnum.Tenant}
          isModalOpen={isModalOpen}
          setModalOpen={setModalOpen}
        />
      )}

      {/* Add PAYMENT*/}
      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig((prev) => ({ ...prev, isOpen: false }));
            if (onPaymentAdded) {
              onPaymentAdded();
            }
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode="2"
        />
      )}

      {/* Confirmation Popup for Delete */}
      {showConfirmation === String(listingData?.id) && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Tenant"
          message="Do you want to delete this tenant?"
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={() => handleDelete?.(listingData?.id)}
          onSecondaryAction={() => setShowConfirmation?.(null)}
          onClose={() => setShowConfirmation?.(null)}
          isPrimaryDestructive={true}
          isLoading={isDeletingTenant}
          loadingLabel="Deleting..."
        />
      )}
      <Commonpopup
        isOpen={isRenewOpen}
        onClose={() => setRenewOpen(false)} // Close popup handler
        component={
          <SelectType
            title="Lease"
            firstOption="E Lease"
            secondOption="Upload Lease Agreement"
            icon1={GetImages.BlueFilledFile}
            icon2={GetImages.BlueFilledFile}
            selectBasic={selectLease}
            setSelectBasic={setSelectLease}
            handleNext={() =>
              navigate("/tenants/renew-lease/" + listingData?.id)
            }
            showNextBtn={false}
          />
        }
      />
    </>
  );
};

export default CurrentTenantCardItem;
